import styled from "styled-components";

const infoYouProvide = [
  "Full name",
  "Email address",
  "Phone number",
  "Any content you upload, share, or save (photos, videos, folios, comments)",
];

const infoCollectedAutomatically = [
  "Device information (device model, operating system, unique device identifier)",
  "IP address",
  "Usage data (how you interact with features, content viewed, session duration)",
  "Authentication data from sign-in methods (Google Sign-In, Apple Sign-In - we only access necessary authentication details)",
];

const cookieUses = [
  "Remember your preferences and login sessions",
  "Analyze traffic and usage patterns",
  "Deliver relevant advertisements and sponsored content",
  "Help measure campaign performance and engagement",
];

const useOfInformation = [
  "Provide, maintain, and personalize your app experience",
  "Manage your account and login",
  "Enable the creation and organization of content (folios, media uploads, etc.)",
  "Monitor and moderate content for safety and compliance",
  "Respond to user reports or community guideline violations",
  "Communicate with you about updates, features, or policy changes",
  "Prevent fraud, misuse, and maintain app security",
  "Deliver targeted advertisements and sponsored content",
  "Improve our services through usage analytics and automated insights",
];

const automatedModeration = [
  "Scanning public content and comments",
  "Flagging potentially harmful, explicit, or policy-breaking behavior",
  "Assisting human moderators in decision-making",
];

const sharingList = [
  "Advertising partners to serve relevant ads based on your activity, preferences, and interactions within the app",
  "Analytics providers to improve app performance and personalization",
  "Third-party service providers for functions like login, hosting, and support",
  "Legal authorities, if required by law or legal proceedings",
  "Successors or partners in the case of a merger, acquisition, or sale",
];

const rightsAndChoices = [
  "View, update, or delete your personal data via account settings",
  "Control your cookie preferences in device or browser settings",
  "Manage ad personalization preferences (if available in the app)",
  "Request full deletion of your account and data by contacting us",
];

const Privacy = () => {
  const effectiveDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const contactEmail =
    process.env.REACT_APP_SUPPORT_EMAIL || "oquetel@creationbesties.com";

  return (
    <PageWrapper>
      <Content>
        <Title>Privacy Policy</Title>
        <EffectiveDate>Effective Date: {effectiveDate}</EffectiveDate>
        <Paragraph>
          This Privacy Policy explains how Creation Besties ("we," "our," or
          "us") collects, uses, and protects the personal information of users
          ("you" or "your") who use the Besties mobile application and related
          services ("Besties" or the "Service").
        </Paragraph>
        <Paragraph>
          By using Besties, you agree to the collection and use of information
          in accordance with this policy.
        </Paragraph>

        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          <SubTitle>a. Information You Provide</SubTitle>
          <List>
            {infoYouProvide.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>

          <SubTitle>b. Information We Collect Automatically</SubTitle>
          <List>
            {infoCollectedAutomatically.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>

          <SubTitle>c. Cookies and Tracking Technologies</SubTitle>
          <Paragraph>
            We use cookies and similar technologies (such as pixels and local
            storage) to:
          </Paragraph>
          <List>
            {cookieUses.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
          <Paragraph>
            By using Besties, you consent to our use of cookies. You can adjust
            your cookie preferences through your device or browser settings.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <List>
            {useOfInformation.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Automated Moderation and AI Use</SectionTitle>
          <Paragraph>
            To maintain a safe and respectful environment, Besties uses
            automated systems and AI technologies to detect content that may
            violate our community guidelines. This includes:
          </Paragraph>
          <List>
            {automatedModeration.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
          <Paragraph>
            These systems are not perfect and do not make final decisions.
            Flagged content may be reviewed by human moderators where
            appropriate.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Private Messaging</SectionTitle>
          <Paragraph>
            Private messages and shared media between users are not monitored
            unless a message or user is reported via in-app tools, or we are
            legally required to review the content. We respect your privacy and
            only intervene when necessary for user safety or legal compliance.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Content Visibility and Sharing</SectionTitle>
          <Paragraph>
            Content posted publicly may be visible to other users. You can
            control visibility using in-app settings.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Sharing of Information</SectionTitle>
          <Paragraph>We do not sell or rent your personal data.</Paragraph>
          <Paragraph>However, we may share limited information with:</Paragraph>
          <List>
            {sharingList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
          <Paragraph>
            Advertising data is shared in a privacy-conscious way and only based
            on your app behavior and themes you follow.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Data Storage and Security</SectionTitle>
          <Paragraph>
            We use industry-standard encryption and security practices to
            protect your personal data. While we strive for full protection, no
            system is 100% secure. Always report suspicious activity.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Your Rights and Choices</SectionTitle>
          <List>
            {rightsAndChoices.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>9. Children's Privacy</SectionTitle>
          <Paragraph>
            Besties is not intended for users under the age of 13. We do not
            knowingly collect personal data from children under 13. If
            discovered, we will delete such data immediately.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. OTP Verification and Twilio Messaging</SectionTitle>
          <Paragraph>
            When verifying your phone number during sign-up, password reset, or
            similar security flows, we send a one-time passcode (OTP) to your
            device via SMS using Twilio. By providing your phone number and
            requesting verification, you consent to receiving these OTP messages
            for account security purposes. Carrier messaging rates may apply.
            OTPs are used solely for verification and are not retained longer
            than necessary to complete the process.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Third-Party Logins</SectionTitle>
          <Paragraph>
            When using Apple or Google Sign-In, you grant permission for those
            platforms to share limited basic information (for example, name and
            email) strictly for authentication purposes. We do not access full
            third-party profiles or store passwords.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Changes to This Privacy Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy periodically. If major changes
            occur, we will notify you via the app or email. Your continued use
            of Besties means you accept the revised policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. Contact Us</SectionTitle>
          <Paragraph>
            If you have questions about this Privacy Policy or your data rights,
            contact us at <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
          </Paragraph>
        </Section>

        <Paragraph>
          By using Besties, you agree to this Privacy Policy and the collection,
          use, and sharing of your information as described above.
        </Paragraph>
      </Content>
    </PageWrapper>
  );
};

export default Privacy;

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f7fafc;
  padding: 40px 20px;
`;

const Content = styled.div`
  max-width: 960px;
  margin: 0 auto;
  background-color: #ffffff;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  color: #1a202c;
  line-height: 1.7;
`;

const Title = styled.h1`
  margin: 0 0 12px 0;
  font-size: 32px;
  color: #2d3748;
`;

const EffectiveDate = styled.p`
  margin: 0 0 20px 0;
  font-weight: 600;
  color: #4a5568;
`;

const Section = styled.section`
  margin: 28px 0;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 22px;
  color: #2d3748;
`;

const SubTitle = styled.h3`
  margin: 12px 0 8px 0;
  font-size: 18px;
  color: #2d3748;
`;

const Paragraph = styled.p`
  margin: 8px 0;
  color: #2d3748;
`;

const List = styled.ul`
  margin: 8px 0 12px 18px;
  padding: 0;
  li {
    margin-bottom: 6px;
  }
`;
